import {
    Box,
    IconButton,
    Menu,
    MenuItem,
    TableCell,
    TableRow,
    Theme,
    Typography
} from "@mui/material";
import {
    checkImageExists,
    formatCurrency
} from "../../../../utils/utilityFunctions";
import { useSelector } from "react-redux";
import { RootStateType } from "../../../../redux-store/reducers";
import { useState } from "react";
import {
    ActionIcon,
    BlackDownArrowIcon
} from "../../../../assets/SvgIcons";

const amcIds = ['BDOTR', 'BPIAMC']

function SchemeImageComponent({
    amcId,
    schemeName
}: {
    amcId: string;
    schemeName: string
}) {
    const checkAmcId = amcIds.includes(amcId ?? '') ? amcId?.toLowerCase() : amcId;

    return (
        <Box
            sx={{
                borderRadius: "50%",
                overflow: "hidden",
                width: "35px",
                height: "35px",
                flexShrink: 0,
            }}
        >
            <Box
                component='img'
                src={checkImageExists(`${checkAmcId}`) ? `/images/amcIcons/${checkAmcId}.svg` : `/images/amcIcons/default.svg`}
                alt={schemeName}
                sx={{
                    width: "100%",
                    height: "100%",
                    objectFit: "contain",
                    flexShrink: 0,
                }}
            />
        </Box>
    )
}

function SchemeCell({
    amcId,
    fromScheme,
    toScheme,
    type,
    folioNum
}: {
    amcId: string;
    fromScheme: string;
    toScheme: string;
    type: string;
    folioNum: string;
}) {
    if (type === "STP") {
        return (
            <Box
                sx={{
                    display: "flex",
                    flexDirection: "column",
                    rowGap: "15px",
                }}>
                <Box
                    sx={{
                        display: "flex",
                        columnGap: "12px",
                        alignItems: "center"
                    }}
                >
                    <SchemeImageComponent amcId={amcId} schemeName={fromScheme} />
                    <Typography
                        variant="body_500"
                        sx={(theme) => ({
                            lineHeight: "20px",
                            letterSpacing: "0.02px",
                            color: theme.palette.text.primary,
                            '&:hover': {
                                color: theme.palette.primary[500],
                            },
                        })}
                    >
                        {folioNum ? `${fromScheme} (${folioNum})` : `${fromScheme}`}
                    </Typography>
                </Box>
                <BlackDownArrowIcon />
                <Box
                    sx={{
                        display: "flex",
                        columnGap: "12px",
                        alignItems: "center"
                    }}
                >
                    <SchemeImageComponent amcId={amcId} schemeName={toScheme} />
                    <Typography
                        variant="body_500"
                        sx={(theme) => ({
                            lineHeight: "20px",
                            letterSpacing: "0.02px",
                            color: theme.palette.text.primary,
                            '&:hover': {
                                color: theme.palette.primary[500],
                            },
                        })}
                    >
                        {folioNum ? `${toScheme} (${folioNum})` : `${toScheme}`}
                    </Typography>
                </Box>
            </Box>
        )
    }

    return (
        <Box
            sx={{
                display: "flex",
                columnGap: "12px",
                alignItems: "center"
            }}
        >
            <SchemeImageComponent amcId={amcId} schemeName={fromScheme} />
            <Typography
                variant="body_500"
                sx={(theme) => ({
                    lineHeight: "20px",
                    letterSpacing: "0.02px",
                    color: theme.palette.text.primary,
                    '&:hover': {
                        color: theme.palette.primary[500],
                    },
                })}
            >
                {folioNum ? `${fromScheme} (${folioNum})` : `${fromScheme}`}
            </Typography>
        </Box>
    )
}

export default function SystematicPlansTable({
    data,
    type,
    handlePause,
    handleCancel,
    handleSwitchMandate
}: {
    data: { [key: string]: any }[];
    type: string;
    handlePause: (id: string, type: string) => void
    handleCancel: (id: string, type: string) => void
    handleSwitchMandate: (amount: number | string, id: string) => void
}): JSX.Element {
    const [menuAnchor, setMenuAnchor] = useState<null | HTMLElement>(null);
    const [activeRowId, setActiveRowId] = useState<string | null>(null);
    const [amount, setAmount] = useState<number | string>('');

    const { locale, currencyCode } = useSelector((store: RootStateType) => store.currencyInfo);


    const handleOpenMenu = (event: React.MouseEvent<HTMLElement>, id: string, amount: number | string) => {
        setAmount(amount);
        setMenuAnchor(event.currentTarget);
        setActiveRowId(id);
    };

    const handleCloseMenu = () => {
        setMenuAnchor(null);
        setActiveRowId(null);
    };

    return (
        <>
            {data.map((obj: { [key: string]: any }, index: number) => (
                <TableRow
                    key={`${obj?.scheme}-${index}`}
                    sx={(theme: Theme) => ({
                        '& td': {
                            paddingTop: "16px",
                            paddingBottom: "16px",
                            borderBottom: `0.5px solid ${theme.palette.text.secondary}B2`,
                            ':first-of-type': {
                                paddingLeft: "0px",
                            }
                        },
                    })}
                >
                    <TableCell align="left">
                        <SchemeCell
                            amcId={obj?.amcId ?? ""}
                            fromScheme={obj?.schemeName ?? ""}
                            toScheme={obj?.toSchemeName ?? ""}
                            type={type}
                            folioNum={obj?.folioNo ?? ""}
                        />

                    </TableCell>
                    <TableCell align="center">
                            <Typography
                                variant="body_500"
                                sx={(theme: Theme) => ({
                                    lineHeight: "20px",
                                    letterSpacing: "0.02px",
                                    color: theme.palette.text.primary
                                })}
                            >
                                {obj?.mandateId ?? ""}
                            </Typography>
                        </TableCell>
                    <TableCell align="center">
                        <Typography
                            variant="body_500"
                            sx={(theme: Theme) => ({
                                lineHeight: "20px",
                                letterSpacing: "0.02px",
                                color: theme.palette.text.primary
                            })}
                        >
                            {formatCurrency({ value: Number(obj?.installmentAmount ?? 0), locale, currencyCode })}
                        </Typography>
                    </TableCell>
                    {/* <TableCell
                    align="center"
                    sx={{ paddingLeft: "0px" }}
                >
                    <Typography
                        variant="body_500"
                        sx={(theme: Theme) => ({
                            lineHeight: "20px",
                            letterSpacing: "0.02px",
                            color: theme.palette.text.primary
                        })}
                    >
                        {obj.instalmentNo}
                    </Typography>
                </TableCell> */}
                    <TableCell align="center">
                        <Typography
                            variant="body_500"
                            sx={(theme: Theme) => ({
                                lineHeight: "20px",
                                letterSpacing: "0.02px",
                                color: theme.palette.text.primary
                            })}
                        >
                            {obj?.endDate ?? ""}
                        </Typography>
                    </TableCell>
                    <TableCell align="center">
                        <Typography
                            variant="body_500"
                            sx={(theme: Theme) => ({
                                lineHeight: "20px",
                                letterSpacing: "0.02px",
                                color: theme.palette.text.primary
                            })}
                        >
                            {obj?.status ?? ""}
                        </Typography>
                    </TableCell>
                    <TableCell align="center">
                        <IconButton
                            id="action-button"
                            aria-label="action-button"
                            aria-controls={menuAnchor ? 'action-menu' : undefined}
                            aria-haspopup="true"
                            aria-expanded={menuAnchor ? 'true' : undefined}
                            onClick={(event) => handleOpenMenu(event, obj?.transRefNo, obj?.installmentAmount ?? 0)}
                        >
                            <ActionIcon />
                        </IconButton>
                        <Menu
                            id="action-menu"
                            aria-labelledby="action-button"
                            anchorEl={menuAnchor}
                            open={Boolean(menuAnchor)}
                            onClose={handleCloseMenu}
                            anchorOrigin={{
                                vertical: 'bottom',
                                horizontal: 'center'
                            }}
                            transformOrigin={{
                                vertical: 'top',
                                horizontal: 'center'
                            }}
                            sx={{
                                boxShadow: 'none',
                                '& .MuiPaper-root': {
                                    boxShadow: '0px 2px 4px rgba(0, 0, 0, 0.1)',
                                    maxWidth: '180px',
                                    minWidth: '150px',
                                }
                            }}
                        >
                            {type === 'SIP' && activeRowId && [
                                <MenuItem
                                    key="pause-sip"
                                    onClick={() => { handlePause(activeRowId, 'SIP'); handleCloseMenu(); }}
                                    sx={(theme: Theme) => ({
                                        margin: "2px 5px 5px 5px",
                                        padding: "10px",
                                        '&:hover': {
                                            backgroundColor: theme.palette.primary[50],
                                            borderRadius: "5px"
                                        }
                                    })}
                                >
                                    <Typography
                                        variant="body_500"
                                        sx={(theme: Theme) => ({
                                            lineHeight: "16px",
                                            color: theme.palette.text.secondary,
                                            '&:hover': {
                                                color: theme.palette.primary[500]
                                            }
                                        })}
                                    >
                                        Pause SIP
                                    </Typography>
                                </MenuItem>,
                                <MenuItem
                                    key="cancel-sip"
                                    onClick={() => { handleCancel(activeRowId, 'SIP'); handleCloseMenu(); }}
                                    sx={(theme: Theme) => ({
                                        margin: "5px 5px 2px 5px",
                                        padding: "10px",
                                        '&:hover': {
                                            backgroundColor: theme.palette.primary[50],
                                            borderRadius: "5px"
                                        }
                                    })}
                                >
                                    <Typography
                                        variant="body_500"
                                        sx={(theme: Theme) => ({
                                            lineHeight: "16px",
                                            color: theme.palette.text.secondary,
                                            '&:hover': {
                                                color: theme.palette.primary[500]
                                            }
                                        })}
                                    >
                                        Cancel SIP
                                    </Typography>
                                </MenuItem>,
                                <MenuItem
                                    key="switch-mandate"
                                    onClick={() => { handleSwitchMandate(amount, activeRowId); handleCloseMenu(); }}
                                    sx={(theme: Theme) => ({
                                        margin: "5px 5px 2px 5px",
                                        padding: "10px",
                                        '&:hover': {
                                            backgroundColor: theme.palette.primary[50],
                                            borderRadius: "5px"
                                        }
                                    })}
                                >
                                    <Typography
                                        variant="body_500"
                                        sx={(theme: Theme) => ({
                                            lineHeight: "16px",
                                            color: theme.palette.text.secondary,
                                            '&:hover': {
                                                color: theme.palette.primary[500]
                                            }
                                        })}
                                    >
                                        Switch Mandate
                                    </Typography>
                                </MenuItem>
                            ]}
                            {type === 'STP' && activeRowId && (
                                <MenuItem
                                    key="cancel-stp"
                                    onClick={() => { handleCancel(activeRowId, 'STP'); handleCloseMenu(); }}
                                    sx={(theme: Theme) => ({
                                        margin: "0px 5px 0px 5px",
                                        padding: "10px",
                                        '&:hover': {
                                            backgroundColor: theme.palette.primary[50],
                                            borderRadius: "5px"
                                        }
                                    })}
                                >
                                    <Typography
                                        variant="body_500"
                                        sx={(theme: Theme) => ({
                                            lineHeight: "16px",
                                            color: theme.palette.text.secondary,
                                            '&:hover': {
                                                color: theme.palette.primary[500]
                                            }
                                        })}
                                    >
                                        Cancel STP
                                    </Typography>
                                </MenuItem>
                            )}
                            {type === 'SWP' && activeRowId && (
                                <MenuItem
                                    key="cancel-swp"
                                    onClick={() => { handleCancel(activeRowId, 'SWP'); handleCloseMenu(); }}
                                    sx={(theme: Theme) => ({
                                        margin: "0px 5px 0px 5px",
                                        padding: "10px",
                                        '&:hover': {
                                            backgroundColor: theme.palette.primary[50],
                                            borderRadius: "5px"
                                        }
                                    })}
                                >
                                    <Typography
                                        variant="body_500"
                                        sx={(theme: Theme) => ({
                                            lineHeight: "16px",
                                            color: theme.palette.text.secondary,
                                            '&:hover': {
                                                color: theme.palette.primary[500]
                                            }
                                        })}
                                    >
                                        Cancel SWP
                                    </Typography>
                                </MenuItem>
                            )}
                        </Menu>
                    </TableCell>
                </TableRow>
            ))}
        </>
    )
}